/**
 * SceneManager - Three.js Scene Management
 * Handles WebGL rendering, scene setup, camera, and lighting
 *
 * Phase 3.5: Supports both legacy PerspectiveCamera and new OrthographicCamera via CameraController
 */

import * as THREE from 'three';
import { GridRenderer } from './GridRenderer.js';

export class SceneManager {
    constructor(canvas, options = {}) {
        this.canvas = canvas;
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.lights = [];
        this.gridRenderer = null;
        this.gridGroup = null;
        this.rotationPivot = null; // Phase 3.5: Scene rotation pivot

        // Camera mode: 'perspective' (legacy) or 'orthographic' (Phase 3.5)
        this.cameraMode = options.cameraMode || 'perspective';
        this.externalCameraController = options.cameraController || null;

        // Camera state (for legacy perspective mode)
        this.cameraDistance = 5;
        this.cameraAngleX = Math.PI / 4;
        this.cameraAngleY = Math.PI / 6;

        // Mouse interaction state
        this.isDragging = false;
        this.lastMouseX = 0;
        this.lastMouseY = 0;
    }

    /**
     * Initialize Three.js scene, camera, renderer
     */
    init() {
        console.log(`Initializing Three.js scene (camera mode: ${this.cameraMode})...`);

        // Create scene
        this.scene = new THREE.Scene();
        // Match Java background color: SETTINGS.java:129 - color(250, 250, 250)
        this.scene.background = new THREE.Color(0xFAFAFA);

        // Create camera based on mode
        if (this.cameraMode === 'orthographic' && this.externalCameraController) {
            // Use external CameraController (Phase 3.5)
            this.camera = this.externalCameraController.getCamera();
            console.log('Using OrthographicCamera from CameraController');
        } else {
            // Legacy perspective camera
            const aspect = this.canvas.clientWidth / this.canvas.clientHeight;
            this.camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 1000);
            this.updateCameraPosition();
            console.log('Using legacy PerspectiveCamera');
        }

        // Create renderer
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
            alpha: false
        });
        this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        // Add lights
        this.setupLights();

        // Add ground plane
        this.addGroundPlane();

        // Setup resize handler
        window.addEventListener('resize', () => this.onResize());

        // Setup mouse controls (only for legacy mode)
        if (this.cameraMode === 'perspective') {
            this.setupMouseControls();
        }

        console.log('Three.js scene initialized');

        // Update UI status
        const status = document.getElementById('three-status');
        if (status) status.innerHTML = '<span class="status">Ready</span>';
    }

    /**
     * Setup scene lighting
     */
    setupLights() {
        // Base intensities match SceneSettingsPanel DEFAULTS (the panel
        // re-applies persisted settings on startup anyway)
        const ambient = new THREE.AmbientLight(0xffffff, 1.85);
        this.scene.add(ambient);
        this.lights.push(ambient);

        // Directional light (sun)
        const sun = new THREE.DirectionalLight(0xffffff, 1.9);
        sun.position.set(5, 10, 5);
        sun.castShadow = true;
        sun.shadow.camera.left = -10;
        sun.shadow.camera.right = 10;
        sun.shadow.camera.top = 10;
        sun.shadow.camera.bottom = -10;
        sun.shadow.mapSize.width = 2048;
        sun.shadow.mapSize.height = 2048;
        this.scene.add(sun);
        this.lights.push(sun);
        this.sunLight = sun;
        // Frustum half-extent the assembled chair is sized for; flat mode
        // widens it to cover the spread-out parts (see setSunShadowExtent)
        this.defaultSunShadowExtent = 10;

        // Fill light
        const fill = new THREE.DirectionalLight(0x4488ff, 2);
        fill.position.set(-5, 5, -5);
        this.scene.add(fill);
        this.lights.push(fill);
    }

    /**
     * Add ground plane for shadow receiving
     */
    addGroundPlane() {
        // Create a large ground plane that receives shadows
        const geometry = new THREE.PlaneGeometry(100, 100);
        const material = new THREE.ShadowMaterial({
            opacity: 0.3 // Shadows will be visible, but the plane itself is transparent
        });

        const ground = new THREE.Mesh(geometry, material);
        ground.rotation.x = -Math.PI / 2; // Rotate to be horizontal
        ground.position.y = -5; // Position below the chair
        ground.receiveShadow = true; // Enable shadow receiving
        ground.name = 'groundPlane';

        this.scene.add(ground);
        this.groundPlane = ground;

        console.log('Ground plane added for shadow receiving');
    }

    /**
     * Resize the sun's shadow camera frustum so it covers the given
     * world-space half-extent (measured from the origin, where the model
     * and the flat-packed parts are centred). Anything outside the frustum
     * receives no shadow and reads brighter than the shaded parts inside it;
     * the Unassembled preview spreads parts well past the assembled chair's
     * footprint, so it widens this to keep every part lit consistently.
     * Pass null to restore the default (crisp) extent.
     *
     * @param {number|null} halfExtent - Half-width of the square footprint in
     *   world units, or null to reset to defaultSunShadowExtent.
     */
    setSunShadowExtent(halfExtent) {
        if (!this.sunLight) return;
        const e = Math.max(
            this.defaultSunShadowExtent,
            halfExtent == null ? this.defaultSunShadowExtent : halfExtent
        );
        const cam = this.sunLight.shadow.camera;
        cam.left = -e;
        cam.right = e;
        cam.top = e;
        cam.bottom = -e;
        cam.updateProjectionMatrix();
    }

    /**
     * Setup mouse controls for camera
     */
    setupMouseControls() {
        // Use pointer events for desktop + mobile compatibility
        this.canvas.addEventListener('pointerdown', (e) => {
            this.isDragging = true;
            this.lastMouseX = e.clientX;
            this.lastMouseY = e.clientY;
        });

        this.canvas.addEventListener('pointermove', (e) => {
            if (!this.isDragging) return;

            const deltaX = e.clientX - this.lastMouseX;
            const deltaY = e.clientY - this.lastMouseY;

            this.cameraAngleY -= deltaX * 0.01;
            this.cameraAngleX -= deltaY * 0.01;

            // Clamp vertical angle
            this.cameraAngleX = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, this.cameraAngleX));

            this.updateCameraPosition();

            this.lastMouseX = e.clientX;
            this.lastMouseY = e.clientY;
        });

        this.canvas.addEventListener('pointerup', () => {
            this.isDragging = false;
        });

        this.canvas.addEventListener('pointerleave', () => {
            this.isDragging = false;
        });

        // Mouse wheel for zoom
        this.canvas.addEventListener('wheel', (e) => {
            e.preventDefault();
            this.cameraDistance += e.deltaY * 0.01;
            this.cameraDistance = Math.max(2, Math.min(20, this.cameraDistance));
            this.updateCameraPosition();
        });
    }

    /**
     * Update camera position based on angles
     */
    updateCameraPosition() {
        if (!this.camera) return;

        const x = Math.cos(this.cameraAngleY) * Math.cos(this.cameraAngleX) * this.cameraDistance;
        const y = Math.sin(this.cameraAngleX) * this.cameraDistance;
        const z = Math.sin(this.cameraAngleY) * Math.cos(this.cameraAngleX) * this.cameraDistance;

        this.camera.position.set(x, y, z);
        this.camera.lookAt(0, 0, 0);
    }

    /**
     * Handle window resize
     */
    onResize() {
        const width = this.canvas.clientWidth;
        const height = this.canvas.clientHeight;

        if (this.cameraMode === 'orthographic' && this.externalCameraController) {
            // Update external camera controller
            this.externalCameraController.resize(width, height);
        } else {
            // Update perspective camera
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        }

        this.renderer.setSize(width, height);
    }

    /**
     * Render scene
     */
    render() {
        this.renderer.render(this.scene, this.camera);
    }

    /**
     * Capture a PNG thumbnail of the current view (cloud Save/Publish).
     * Renders synchronously first: the WebGL context has no
     * preserveDrawingBuffer, so the drawing buffer is only readable in the
     * same task as a render.
     * @param {number} maxW - Max thumbnail width in px
     * @returns {string|null} PNG data URL
     */
    captureThumbnail(maxW = 480) {
        try {
            this.renderer.render(this.scene, this.camera);
            const src = this.renderer.domElement;
            if (!src.width || !src.height) return null;
            const scale = Math.min(1, maxW / src.width);
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(src.width * scale));
            canvas.height = Math.max(1, Math.round(src.height * scale));
            canvas.getContext('2d')
                .drawImage(src, 0, 0, canvas.width, canvas.height);
            return canvas.toDataURL('image/png');
        } catch (e) {
            console.warn('captureThumbnail failed:', e);
            return null;
        }
    }

    /**
     * Add object to scene
     */
    add(object) {
        // Phase 3.5: Add to rotation pivot if it exists
        if (this.rotationPivot) {
            this.rotationPivot.add(object);
        } else {
            this.scene.add(object);
        }
    }

    /**
     * Remove object from scene
     */
    remove(object) {
        if (this.rotationPivot) {
            this.rotationPivot.remove(object);
        } else {
            this.scene.remove(object);
        }
    }

    /**
     * Set rotation pivot group (Phase 3.5)
     * All objects added after this will be children of the pivot
     * @param {THREE.Group} pivotGroup - Rotation pivot group
     */
    setRotationPivot(pivotGroup) {
        this.rotationPivot = pivotGroup;
        this.scene.add(pivotGroup);
    }

    /**
     * Cleanup
     */
    dispose() {
        this.renderer.dispose();
        this.scene.clear();
    }
}
